import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useHalachicData } from '../../hooks/useHalachicData';
import {
  getOmerCountText,
  getSefirahName,
  getOmerDayHebrew,
  getWeekData,
  getDaysUntilOmer,
  getPreviewCountdownText,
  BRACHA_TEXT,
  LESHEM_YICHUD_LINES,
  PASUK_VE_SAFARTEM,
  PASUK_SHEVA_SHABATOT,
  HARACHAMAN_TEXT,
  PSALM_67_VERSES,
  ANA_BKOACH_LINES,
  OFF_SEASON_MESSAGE,
} from './omerUtils';
import templates from './templates';

/**
 * OmerWidget
 * מציג את ספירת העומר עם עיצוב ועשירות טקסט לפי הגדרות.
 * מתעדכן אוטומטית בשקיעה (דרך useHalachicData).
 *
 * Props:
 *   location             — { lat, lng, timezone } מ-Dashboard
 *   nusach               — 'ashkenaz' | 'sephardic' | 'mizrahi'
 *   showBracha           — האם להציג ברכה
 *   showFullNusach       — האם להציג שבועות+ימים
 *   showLeshemYichud     — האם להציג לשם ייחוד
 *   showPasuk            — 'none' | 'veSafartem' | 'shevaShabatot'
 *   showAfterCount       — האם להציג תפילות אחרי ספירה
 *   template             — שם התבנית
 *   title / showTitle    — כותרת
 */
export const OmerWidget = ({
  // Legacy props kept for backward compatibility (widgetId, omerData, dispatch no longer used)
  widgetId,
  omerData: legacyOmerData,
  loading: legacyLoading,
  error: legacyError,
  dispatch: legacyDispatch,
  location,
  nusach           = 'ashkenaz',
  showBracha       = true,
  showFullNusach   = true,
  showLeshemYichud = false,
  showPasuk        = 'none',
  showAfterCount   = false,
  template         = 'Elegant',
  title            = 'ספירת העומר',
  showTitle        = false,
  showPreview      = false,
  previewOmerDay   = null,
  bgColor,
}) => {
  // ── שליפה מהמנוע ההלכתי ──────────────────────────────────────────────
  const engineConfig = {
    location: {
      lat: location?.lat,
      lng: location?.lng,
      timezone: location?.timezone || location?.tzid || 'Asia/Jerusalem',
    },
    isIsrael: true,
    edah: nusach === 'mizrahi' ? 'mizrahi' : nusach === 'sephardic' ? 'sephardic' : 'ashkenaz',
  };

  const { today, loading: engineLoading, error: engineError } = useHalachicData(engineConfig);

  // ── חישוב נתוני עומר מתוך המנוע ──────────────────────────────────────
  // previewOmerDay דורס את המנוע — לתצוגה מקדימה של תבניות
  const omerDay = previewOmerDay ?? today?.seasonalCustoms?.omerDay ?? null;
  const isOmerSeason = previewOmerDay ? true : (today?.seasonalCustoms?.isOmerSeason ?? false);

  // ── מצב תצוגה מקדימה (preview) — מציג יום 1 + ספירה לאחור ────────────
  const isPreview = !isOmerSeason && showPreview;
  const effectiveDay = isPreview ? 1 : omerDay;

  const { mainLine, subLine } = effectiveDay
    ? getOmerCountText(effectiveDay, nusach)
    : { mainLine: '', subLine: null };

  const sefirahName  = effectiveDay ? getSefirahName(effectiveDay)      : '';
  const omerDayHeb   = effectiveDay ? getOmerDayHebrew(effectiveDay)    : '';
  const weekData     = effectiveDay ? getWeekData(effectiveDay)         : { weeks: 0, dayInWeek: 0 };

  // ── ספירה לאחור ────────────────────────────────────────────────────────
  let previewCountdown = null;
  if (isPreview && today?.hebrewDate) {
    const daysLeft = getDaysUntilOmer(
      today.hebrewDate.month,
      today.hebrewDate.day,
      today.hebrewDate.isLeapYear,
    );
    if (daysLeft != null && daysLeft > 0) {
      previewCountdown = getPreviewCountdownText(daysLeft);
    }
  }

  const pasukText =
    showPasuk === 'veSafartem'    ? PASUK_VE_SAFARTEM    :
    showPasuk === 'shevaShabatot' ? PASUK_SHEVA_SHABATOT : null;

  const afterCountSections = showAfterCount ? [
    { id: 'harachaman', title: 'הָרַחֲמָן', lines: [{ id: 'hr1', text: HARACHAMAN_TEXT }] },
    { id: 'psalm67',    title: 'תהילים סז', lines: PSALM_67_VERSES },
    { id: 'anaBkoach',  title: 'אָנָּא בְּכֹחַ', lines: ANA_BKOACH_LINES },
  ] : [];

  const Template = templates[template] || templates['Elegant'];

  if (!previewOmerDay && engineLoading && !today) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={24} sx={{ color: '#c8a84b' }} />
      </Box>
    );
  }

  if (!previewOmerDay && engineError && !today) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#ef4444' }}>שגיאה בטעינת הנתונים</Typography>
      </Box>
    );
  }

  return (
    <Template
      // נתוני ספירה
      omerDay={effectiveDay}
      isOmerSeason={isOmerSeason || isPreview}
      isPreview={isPreview}
      previewCountdown={previewCountdown}
      mainCountText={mainLine}
      subCountText={showFullNusach ? subLine : null}
      omerDayHebrew={omerDayHeb}
      sefirahName={sefirahName}
      weekNumber={weekData.weeks + (weekData.dayInWeek >= 1 ? 1 : 0)}
      dayInWeek={weekData.dayInWeek}
      // הגדרות תצוגה
      showBracha={showBracha}
      brachaText={BRACHA_TEXT}
      showLeshemYichud={showLeshemYichud}
      leshemYichudLines={LESHEM_YICHUD_LINES}
      showPasuk={showPasuk}
      pasukText={pasukText}
      showAfterCount={showAfterCount}
      afterCountSections={afterCountSections}
      offSeasonMessage={OFF_SEASON_MESSAGE}
      // כותרת ועיצוב
      title={title}
      showTitle={showTitle}
      bgColor={bgColor}
    />
  );
};
